import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { components } from "../../types/api";
import {
  useGetCampaignMetricsQuery,
  useGetCampaignByIdQuery,
} from "../../store/apis/campaignApi";
import { useGetMetricsQuery } from "../../store/apis/internalCampaignsMergeApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { exportMetricsToCSV } from "../../utils/exportUtils";
import InternalCampaignMetricsTable from "../table/InternalCampaignMetricsTable";

interface InternalCampaignMetrics {
  impressions: number;
  clicks: number;
  ctr: number;
  cpm: number;
  cpc: number;
  cost: number;
  realCost?: number;
  cpv?: number;
  vtr?: number;
  dailyInvestment?: number;
  dailyImpressions?: number;
  completeViews?: number;
  dailyCTR?: number;
  conversions?: number;
  date?: string;
}

type EntityType = "MERGE" | "AD_ORDER";

type MergedGroup =
  components["schemas"]["MergedCampaignMetricsBreakdown"]["mergedGroups"][number];
type InsertionOrderWithMetrics = NonNullable<
  MergedGroup["insertionOrders"]
>[number] & {
  metrics?: components["schemas"]["MetricsLineItemSummary"];
};

interface InternalCampaign {
  id: string;
  displayName: string;
  internalPlatform?: string;
  platform?: string;
  internalId?: string;
  margin?: number;
  startDate?: string | null;
  endDate?: string | null;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  timezone?: string;
  status?: components["schemas"]["CampaignStatus"];
  metrics?: InternalCampaignMetrics;
  errorSync?: string | null;
  lastSyncAt?: string | null;
  parentMergeId?: string;
  entityType?: EntityType;
  insertionOrderId?: string;
}

type Campaign = components["schemas"]["CampaignWithRelations"];

interface CampaignInternalCampaignsPanelProps {
  campaign: Campaign;
  isAdmin: boolean;
  isClient: boolean;
  maxHeight?: string | number;
}

const CampaignInternalCampaignsPanel: React.FC<
  CampaignInternalCampaignsPanelProps
> = ({ campaign, isAdmin, isClient, maxHeight = 320 }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data: campaignMetricsResponse,
    error: metricsError,
    isFetching: isFetchingMetrics,
  } = useGetCampaignMetricsQuery(
    { campaignId: campaign.id || "" },
    { skip: !campaign.id }
  );

  const { error: campaignDetailError, isFetching: isFetchingCampaignDetail } =
    useGetCampaignByIdQuery(
      { campaignId: campaign.id || "" },
      { skip: !campaign.id }
    );

  const {
    data: campaignMetricsBreakdownData,
    error: campaignMetricsBreakdownError,
    isFetching: isFetchingCampaignMetricsBreakdown,
  } = useGetMetricsQuery(
    { campaignId: campaign.id || "" },
    { skip: !campaign.id }
  );

  const isSyncingInternalData =
    isFetchingCampaignDetail || isFetchingCampaignMetricsBreakdown;

  const processedInternalCampaignsData = useMemo(() => {
    if (
      isSyncingInternalData ||
      !campaignMetricsBreakdownData?.mergedGroups?.length
    ) {
      return [];
    }

    const rows: InternalCampaign[] = [];

    const summarizeMetrics = (
      summary?:
        | components["schemas"]["MetricsSummary"]
        | components["schemas"]["MetricsLineItemSummary"]
    ): InternalCampaignMetrics | undefined => {
      if (!summary) {
        return undefined;
      }

      const safeValue = (value?: number) => value ?? 0;

      return {
        impressions: safeValue(summary.totalImpressions),
        clicks: safeValue(summary.totalClicks),
        ctr: safeValue(summary.avgCTR),
        cpm: safeValue(summary.avgCPM),
        cpc: safeValue(summary.avgCPC),
        cost: safeValue(summary.totalCost),
        realCost: summary.totalRealCost,
        completeViews: safeValue(summary.totalCompleteViews),
        conversions: safeValue(summary.totalConversions),
        dailyInvestment: safeValue(summary.totalCost) / 30,
        dailyImpressions: safeValue(summary.totalImpressions) / 30,
        dailyCTR: safeValue(summary.avgCTR),
      };
    };

    campaignMetricsBreakdownData.mergedGroups.forEach((group) => {
      const shouldDisplayInsertionOrders =
        Boolean(group.insertionOrders?.length) &&
        group.internalCampaigns?.some((internalCampaign) =>
          (internalCampaign?.platform || "").toUpperCase().includes("DV360")
        );

      const syncMetadata = group.internalCampaigns?.[0];

      if (shouldDisplayInsertionOrders && group.insertionOrders) {
        (group.insertionOrders as InsertionOrderWithMetrics[]).forEach(
          (order) => {
            const lineItemSummary = summarizeMetrics(order.metrics);

            rows.push({
              id: `${group.id}-${order.id}`,
              parentMergeId: group.id,
              displayName: order.name || `IO: ${order.id}`,
              internalPlatform: group.platform || "DV360",
              platform: group.platform || "DV360",
              metrics: lineItemSummary,
              entityType: "AD_ORDER",
              insertionOrderId: order.insertionOrderId,
              status: group.status,
              lastSyncAt: syncMetadata?.lastSyncAt,
              errorSync: syncMetadata?.errorSync,
              startDate: syncMetadata?.startDate,
              endDate: syncMetadata?.endDate,
            });
          }
        );
        return;
      }

      const groupSummary = summarizeMetrics(group.metrics?.summary);

      rows.push({
        id: group.id,
        displayName: group.name || t("campaigns.defaultGroup"),
        platform: group.platform,
        internalPlatform: group.platform,
        metrics: groupSummary,
        status: group.status,
        entityType: "MERGE",
        lastSyncAt: syncMetadata?.lastSyncAt,
        errorSync: syncMetadata?.errorSync,
        startDate: syncMetadata?.startDate,
        endDate: syncMetadata?.endDate,
      });
    });

    return rows;
  }, [campaignMetricsBreakdownData, t, isSyncingInternalData]);

  useEffect(() => {
    if (metricsError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.metricsError", {
            defaultValue: "Failed to load campaign metrics",
          }),
          severity: "error",
        })
      );
    }
  }, [metricsError, dispatch, t]);

  useEffect(() => {
    if (campaignDetailError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.detailError", {
            defaultValue: "Failed to load campaign details",
          }),
          severity: "error",
        })
      );
    }
  }, [campaignDetailError, dispatch, t]);

  useEffect(() => {
    if (campaignMetricsBreakdownError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.metricsBreakdownError", {
            defaultValue: "Failed to load campaign metrics data",
          }),
          severity: "error",
        })
      );
    }
  }, [campaignMetricsBreakdownError, dispatch, t]);

  const handleExportMetrics = () => {
    exportMetricsToCSV(
      campaignMetricsResponse?.data?.metrics,
      campaign.displayName || "export",
      t,
      dispatch
    );
  };

  return (
    <InternalCampaignMetricsTable
      internalCampaigns={processedInternalCampaignsData}
      isLoading={isSyncingInternalData}
      maxHeight={maxHeight}
      isAdmin={isAdmin}
      canViewInsertionOrderId={!isClient}
      onExport={handleExportMetrics}
      exportDisabled={
        isFetchingMetrics || !campaignMetricsResponse?.data?.metrics?.length
      }
      onInternalCampaignClick={(internalCampaign) => {
        const mergeId = internalCampaign.parentMergeId || internalCampaign.id;
        navigate(`/campaign/${campaign.id}/${mergeId}`);
      }}
      onInternalCampaignOpenInNewTab={(internalCampaign) => {
        const mergeId = internalCampaign.parentMergeId || internalCampaign.id;
        window.open(`/campaign/${campaign.id}/${mergeId}`, "_blank");
      }}
    />
  );
};

export default CampaignInternalCampaignsPanel;
